// src/BusinessSettings.js
import React, { useState, useEffect } from 'react';
import TitleBar from './components/ui/TitleBar';
import { getApiBase } from './apiBase';
import { getTenantHeaders } from './api';

const API_BASE = getApiBase();

// ── Mobile helper ──────────────────────────────────────────────────────────
const getIsMobile = () => typeof window !== 'undefined' && window.innerWidth < 1024;

const BusinessSettings = ({ navigate, isPhone }) => {
  const [isMobile, setIsMobile] = useState(getIsMobile);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const [settings, setSettings] = useState({
    businessName: '',
    phone: '',
    mobile: '',
    email: '',
    website: '',
    address: '',
    city: '',
    state: '',
    zip: '',

    taxId: '',
    showTaxIdOnInvoice: false,
    showTaxIdOnEstimate: false,

    businessLicense: '',
    showBusinessLicenseOnDocs: false,
    insurancePolicy: '',
    showInsurancePolicyOnDocs: false,

    starting_invoice_number: 1,
    starting_estimate_number: 1
  });

  // ── Booking link slug ──────────────────────────────────────────────────────
  const [bookingSlug, setBookingSlug] = useState('');
  const [linkCopied, setLinkCopied] = useState(false);

  // ── Account state ─────────────────────────────────────────────────────────
  const [pwForm, setPwForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [pwSaving, setPwSaving] = useState(false);
  const [pwMsg, setPwMsg] = useState(null);
  const [showPwCurrent, setShowPwCurrent] = useState(false);
  const [showPwNew, setShowPwNew] = useState(false);
  const [showPwConfirm, setShowPwConfirm] = useState(false);

  const [emForm, setEmForm] = useState({ currentPassword: '', newEmail: '' });
  const [emSaving, setEmSaving] = useState(false);
  const [emMsg, setEmMsg] = useState(null);
  const [showEmPw, setShowEmPw] = useState(false);

  const currentEmail = localStorage.getItem('user_email') || '';

  // ── Responsive listener ────────────────────────────────────────────────────
  useEffect(() => {
    const onResize = () => setIsMobile(getIsMobile());
    window.addEventListener('resize', onResize);
    setIsMobile(getIsMobile()); // force correct value after mount
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    loadSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Load settings ──────────────────────────────────────────────────────────
  const loadSettings = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/settings/business`, {
        headers: { ...getTenantHeaders() }
      });
      if (res.ok) {
        const data = await res.json();
        if (data && Object.keys(data).length > 0) {
          setSettings((prev) => ({
            ...prev,
            businessName: data.businessName || '',
            phone: data.phone || '',
            mobile: data.mobile || '',
            email: data.email || '',
            website: data.website || '',
            address: data.address || '',
            city: data.city || '',
            state: data.state || '',
            zip: data.zip || '',

            taxId: data.taxId || '',
            showTaxIdOnInvoice: Number(data.showTaxIdOnInvoice || 0) === 1,
            showTaxIdOnEstimate: Number(data.showTaxIdOnEstimate || 0) === 1,

            businessLicense: data.businessLicense || '',
            showBusinessLicenseOnDocs: Number(data.showBusinessLicenseOnDocs || 0) === 1,
            insurancePolicy: data.insurancePolicy || '',
            showInsurancePolicyOnDocs: Number(data.showInsurancePolicyOnDocs || 0) === 1,

            starting_invoice_number: parseInt(data.starting_invoice_number, 10) || 1,
            starting_estimate_number: parseInt(data.starting_estimate_number, 10) || 1
          }));
          if (data.booking_slug) setBookingSlug(data.booking_slug);
        }
      }
    } catch (error) {
      console.error('Failed to load business settings:', error);
    } finally {
      setLoading(false);
    }
  };

  // ── Helpers ────────────────────────────────────────────────────────────────
  const formatPhone = (value) => {
    const cleaned = String(value || '').replace(/\D/g, '');
    if (cleaned.length <= 3) return cleaned;
    if (cleaned.length <= 6) return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
  };

  const handlePhoneChange = (field, value) => {
    const formatted = formatPhone(value);
    setSettings((prev) => ({ ...prev, [field]: formatted }));
  };

  const setField = (key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  // ── Save ───────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!String(settings.businessName || '').trim()) {
      alert('Business Name is required');
      return;
    }

    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/api/settings/business`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getTenantHeaders() },
        body: JSON.stringify(settings)
      });

      if (!res.ok) throw new Error('Save failed');
      window.dispatchEvent(new CustomEvent('jobflow:saved', {
        detail: { message: 'BUSINESS SETTINGS SAVED!' }
      }));
    } catch (error) {
      console.error('Save error:', error);
      alert('Failed to save settings: ' + (error?.message || 'Unknown error'));
    } finally {
      setSaving(false);
    }
  };

  // ── Change Password ────────────────────────────────────────────────────────
  const handleChangePassword = async () => {
    setPwMsg(null);
    if (!pwForm.currentPassword || !pwForm.newPassword || !pwForm.confirmPassword) {
      return setPwMsg({ type: 'error', text: 'All fields are required.' });
    }
    if (pwForm.newPassword.length < 8) {
      return setPwMsg({ type: 'error', text: 'New password must be at least 8 characters.' });
    }
    if (pwForm.newPassword !== pwForm.confirmPassword) {
      return setPwMsg({ type: 'error', text: 'New passwords do not match.' });
    }
    setPwSaving(true);
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${API_BASE}/api/auth/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ currentPassword: pwForm.currentPassword, newPassword: pwForm.newPassword })
      });
      const data = await res.json();
      if (res.ok) {
        setPwMsg({ type: 'success', text: 'Password updated successfully.' });
        setPwForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      } else {
        setPwMsg({ type: 'error', text: data.error || 'Password change failed.' });
      }
    } catch (e) {
      setPwMsg({ type: 'error', text: 'Network error. Please try again.' });
    } finally {
      setPwSaving(false);
    }
  };

  // ── Change Email ───────────────────────────────────────────────────────────
  const handleChangeEmail = async () => {
    setEmMsg(null);
    if (!emForm.currentPassword || !emForm.newEmail) {
      return setEmMsg({ type: 'error', text: 'All fields are required.' });
    }
    setEmSaving(true);
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${API_BASE}/api/auth/change-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ currentPassword: emForm.currentPassword, newEmail: emForm.newEmail })
      });
      const data = await res.json();
      if (res.ok) {
        setEmMsg({ type: 'success', text: 'Email updated. Please log in again with your new email.' });
        localStorage.setItem('user_email', emForm.newEmail);
        setEmForm({ currentPassword: '', newEmail: '' });
        setTimeout(() => {
          localStorage.removeItem('auth_token');
          localStorage.removeItem('user_email');
          localStorage.removeItem('user_role');
          localStorage.removeItem('user_id');
          window.location.reload();
        }, 2000);
      } else {
        setEmMsg({ type: 'error', text: data.error || 'Email change failed.' });
      }
    } catch (e) {
      setEmMsg({ type: 'error', text: 'Network error. Please try again.' });
    } finally {
      setEmSaving(false);
    }
  };

  // ── Styles ─────────────────────────────────────────────────────────────────
  const styles = {
    page: {
      padding: isPhone ? '18px 4px' : (isMobile ? '18px' : '40px'),
      backgroundColor: '#E8E2DA',
      minHeight: '100vh'
    },
    contentWrapper: {
      maxWidth: '1000px',
      padding: isPhone ? '0' : '0 12px',
      margin: '0 auto'
    },
    panel: {
      backgroundColor: '#E7B888',
      border: isMobile ? '6px solid black' : '10px solid black',
      padding: isPhone ? '18px 5px' : (isMobile ? '18px' : '40px'),
      marginBottom: '18px'
    },
    sectionTitle: {
      fontWeight: 900,
      textTransform: 'uppercase',
      fontSize: isMobile ? '18px' : '22px',
      margin: '30px 0 20px 0',
      paddingTop: '20px',
      borderTop: '4px solid black'
    },
    label: {
      fontWeight: 900,
      textTransform: 'uppercase',
      fontSize: '14px',
      marginBottom: '8px',
      display: 'block'
    },
    input: {
      padding: '14px',
      border: '4px solid black',
      fontWeight: 700,
      width: '100%',
      fontSize: '16px',
      boxSizing: 'border-box',
      backgroundColor: 'white'
    },
    row2: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
      gap: '16px',
      marginBottom: '16px'
    },
    row3: {
      display: 'grid',
      gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr',
      gap: '16px',
      marginBottom: '16px'
    },
    row1: { marginBottom: '16px' },
    toggleRow: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      marginBottom: '12px',
      padding: '14px',
      border: '4px solid black',
      backgroundColor: 'white'
    },
    toggleLabel: {
      fontWeight: 900,
      fontSize: '14px',
      flex: 1,
      textTransform: 'uppercase'
    },
    toggle: {
      width: '60px',
      height: '32px',
      backgroundColor: '#ddd',
      border: '3px solid black',
      borderRadius: '16px',
      position: 'relative',
      cursor: 'pointer',
      transition: 'background-color 0.2s'
    },
    toggleActive: { backgroundColor: '#E8761D' },
    toggleKnob: {
      width: '24px',
      height: '24px',
      backgroundColor: 'black',
      borderRadius: '50%',
      position: 'absolute',
      top: '1px',
      transition: 'left 0.2s'
    },
    saveBtn: {
      background: 'black',
      color: '#FFA500',
      border: '4px solid #E8761D',
      padding: '16px 32px',
      fontWeight: 900,
      cursor: 'pointer',
      textTransform: 'uppercase',
      fontSize: '18px',
      width: '100%'
    },
    note: {
      fontWeight: 900,
      fontSize: '13px',
      padding: '12px',
      border: '3px solid black',
      backgroundColor: '#ffffcc',
      marginBottom: '12px',
      textAlign: 'center'
    },
    loadingBox: {
      padding: '30px',
      textAlign: 'center',
      fontWeight: 900,
      fontSize: '16px'
    },
    msgSuccess: {
      backgroundColor: '#dcfce7',
      border: '3px solid #22c55e',
      color: '#166534',
      padding: '12px',
      fontWeight: 900,
      marginBottom: '14px'
    },
    msgError: {
      backgroundColor: '#fee2e2',
      border: '3px solid #ef4444',
      color: '#991b1b',
      padding: '12px',
      fontWeight: 900,
      marginBottom: '14px'
    },
    currentEmailRow: {
      backgroundColor: '#f0f4ff',
      border: '3px solid #2da8ff',
      padding: '12px',
      fontWeight: 900,
      marginBottom: '18px',
      fontSize: '14px'
    }
  };

  const Toggle = ({ label, value, onChange }) => (
    <div style={styles.toggleRow}>
      <div style={styles.toggleLabel}>{label}</div>
      <div
        style={{ ...styles.toggle, ...(value ? styles.toggleActive : {}) }}
        onClick={() => onChange(!value)}
      >
        <div style={{ ...styles.toggleKnob, left: value ? '31px' : '1px' }} />
      </div>
    </div>
  );

  const eyeBtn = {
    position: 'absolute', right: '12px', top: '50%',
    transform: 'translateY(-50%)', background: 'none',
    border: 'none', cursor: 'pointer', color: '#888',
    fontSize: '18px', padding: '0', lineHeight: 1
  };

  const bookingLink = bookingSlug
    ? `${window.location.origin}/book/${bookingSlug}`
    : '';

  const handleCopyLink = () => {
    if (!bookingLink) return;
    navigator.clipboard.writeText(bookingLink).then(() => {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2500);
    });
  };

  return (
    <div style={styles.page}>
      <style>{`
        .jf-biz-hover { transition: filter 120ms ease, transform 120ms ease; }
        .jf-biz-hover:hover {
          background: #FFC107 !important;
          color: #000 !important;
          border-color: #FFA500 !important;
        }
        .jf-biz-hover:active { transform: translateY(1px); }
      `}</style>

      <div style={styles.contentWrapper}>
        <TitleBar
          icon="🏢"
          title="BUSINESS SETTINGS"
          accent="#E8761D"
          right={
            <button
              className="jf-biz-hover"
              onClick={() => navigate('/settings')}
              style={{
                background: 'black',
                color: '#FFA500',
                border: '5px solid #E8761D',
                padding: '14px',
                fontWeight: 900,
                cursor: 'pointer',
                textTransform: 'uppercase'
              }}
            >
              BACK TO SETTINGS
            </button>
          }
        />

        <div style={styles.panel}>
          {loading ? (
            <div style={styles.loadingBox}>⏳ LOADING BUSINESS SETTINGS...</div>
          ) : (
            <>
              <div style={styles.note}>
                THIS DATA APPEARS ON INVOICES AND ESTIMATES FOR **MYTOOL NOW**.
              </div>

              <div style={styles.row1}>
                <label style={styles.label}>BUSINESS NAME *</label>
                <input
                  style={styles.input}
                  value={settings.businessName}
                  onChange={(e) => setField('businessName', e.target.value)}
                  placeholder="Your Business Name"
                />
              </div>

              <div style={styles.row2}>
                <div>
                  <label style={styles.label}>OFFICE PHONE *</label>
                  <input
                    style={styles.input}
                    value={settings.phone}
                    onChange={(e) => handlePhoneChange('phone', e.target.value)}
                    placeholder="(555) 555-5555"
                  />
                </div>
                <div>
                  <label style={styles.label}>MOBILE PHONE</label>
                  <input
                    style={styles.input}
                    value={settings.mobile}
                    onChange={(e) => handlePhoneChange('mobile', e.target.value)}
                    placeholder="(555) 555-5555"
                  />
                </div>
              </div>

              <div style={styles.row2}>
                <div>
                  <label style={styles.label}>EMAIL *</label>
                  <input
                    style={styles.input}
                    type="email"
                    value={settings.email}
                    onChange={(e) => setField('email', e.target.value)}
                    placeholder="info@yourbusiness.com"
                  />
                </div>
                <div>
                  <label style={styles.label}>WEBSITE</label>
                  <input
                    style={styles.input}
                    value={settings.website}
                    onChange={(e) => setField('website', e.target.value)}
                    placeholder="https://www.yourbusiness.com"
                  />
                </div>
              </div>

              <h2 style={styles.sectionTitle}>ADDRESS (OPTIONAL)</h2>

              <div style={styles.row1}>
                <label style={styles.label}>STREET ADDRESS</label>
                <input
                  style={styles.input}
                  value={settings.address}
                  onChange={(e) => setField('address', e.target.value)}
                  placeholder="123 Main Street"
                />
              </div>

              <div style={styles.row3}>
                <div>
                  <label style={styles.label}>CITY</label>
                  <input
                    style={styles.input}
                    value={settings.city}
                    onChange={(e) => setField('city', e.target.value)}
                    placeholder="Your City"
                  />
                </div>
                <div>
                  <label style={styles.label}>STATE</label>
                  <input
                    style={styles.input}
                    value={settings.state}
                    onChange={(e) => setField('state', String(e.target.value || '').toUpperCase())}
                    placeholder="FL"
                    maxLength={2}
                  />
                </div>
                <div>
                  <label style={styles.label}>ZIP</label>
                  <input
                    style={styles.input}
                    value={settings.zip}
                    onChange={(e) => setField('zip', e.target.value)}
                    placeholder="12345"
                  />
                </div>
              </div>

              <h2 style={styles.sectionTitle}>TAX ID / EIN (OPTIONAL)</h2>

              <div style={styles.row1}>
                <label style={styles.label}>TAX ID / EIN</label>
                <input
                  style={styles.input}
                  value={settings.taxId}
                  onChange={(e) => setField('taxId', e.target.value)}
                  placeholder="12-3456789"
                />
              </div>

              <Toggle
                label="SHOW TAX ID ON INVOICES & ESTIMATES"
                value={settings.showTaxIdOnInvoice || settings.showTaxIdOnEstimate}
                onChange={(val) => { setField('showTaxIdOnInvoice', val); setField('showTaxIdOnEstimate', val); }}
              />

              <h2 style={styles.sectionTitle}>BUSINESS LICENSE (OPTIONAL)</h2>

              <div style={styles.row1}>
                <label style={styles.label}>BUSINESS LICENSE #</label>
                <input
                  style={styles.input}
                  value={settings.businessLicense}
                  onChange={(e) => setField('businessLicense', e.target.value)}
                  placeholder="License number"
                />
              </div>

              <Toggle
                label="SHOW BUSINESS LICENSE # ON INVOICES & ESTIMATES"
                value={settings.showBusinessLicenseOnDocs}
                onChange={(val) => setField('showBusinessLicenseOnDocs', val)}
              />

              <h2 style={styles.sectionTitle}>INSURANCE POLICY (OPTIONAL)</h2>

              <div style={styles.row1}>
                <label style={styles.label}>INSURANCE POLICY #</label>
                <input
                  style={styles.input}
                  value={settings.insurancePolicy}
                  onChange={(e) => setField('insurancePolicy', e.target.value)}
                  placeholder="Policy number"
                />
              </div>

              <Toggle
                label="SHOW INSURANCE POLICY # ON INVOICES & ESTIMATES"
                value={settings.showInsurancePolicyOnDocs}
                onChange={(val) => setField('showInsurancePolicyOnDocs', val)}
              />

              <h2 style={styles.sectionTitle}>INVOICE & ESTIMATE NUMBERING (OPTIONAL)</h2>

              <div style={styles.note}>
                YOUR NEXT INVOICE OR ESTIMATE WILL USE WHICHEVER NUMBER IS HIGHER — THIS SETTING OR YOUR CURRENT HIGHEST EXISTING NUMBER. THIS WILL NEVER RENUMBER EXISTING RECORDS.
              </div>

              <div style={styles.row2}>
                <div>
                  <label style={styles.label}>STARTING INVOICE #</label>
                  <input
                    style={styles.input}
                    type="number"
                    min="1"
                    value={settings.starting_invoice_number}
                    onChange={(e) => setField('starting_invoice_number', parseInt(e.target.value, 10) || 1)}
                    placeholder="1000"
                  />
                </div>
                <div>
                  <label style={styles.label}>STARTING ESTIMATE #</label>
                  <input
                    style={styles.input}
                    type="number"
                    min="1"
                    value={settings.starting_estimate_number}
                    onChange={(e) => setField('starting_estimate_number', parseInt(e.target.value, 10) || 1)}
                    placeholder="1000"
                  />
                </div>
              </div>

              <div style={{ marginTop: '30px' }}>
                <button
                  className="jf-biz-hover"
                  style={styles.saveBtn}
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? 'SAVING...' : 'SAVE BUSINESS SETTINGS'}
                </button>
              </div>
            </>
          )}
        </div>

        {/* ── ONLINE BOOKING LINK ── */}
        {bookingSlug ? (
          <div style={styles.panel}>
            <h2 style={{ ...styles.sectionTitle, marginTop: 0, paddingTop: 0, borderTop: 'none' }}>
              🔗 YOUR ONLINE BOOKING LINK
            </h2>
            <div style={{
              backgroundColor: '#f0fff4',
              border: '4px solid #E8761D',
              padding: '16px',
              marginBottom: '14px',
              fontWeight: 900,
              fontSize: isMobile ? '13px' : '15px',
              wordBreak: 'break-all'
            }}>
              {bookingLink}
            </div>
            <div style={{ ...styles.note, marginBottom: '14px' }}>
              SHARE THIS LINK ON YOUR GOOGLE BUSINESS PROFILE, FACEBOOK PAGE, OR WEBSITE. CUSTOMERS CAN BOOK DIRECTLY WITHOUT CALLING.
            </div>
            <button
              className="jf-biz-hover"
              style={styles.saveBtn}
              onClick={handleCopyLink}
            >
              {linkCopied ? '✅ LINK COPIED!' : '📋 COPY BOOKING LINK'}
            </button>
          </div>
        ) : null}

        {/* ── ACCOUNT — Change Password & Email ── */}
        <div style={styles.panel}>
          <h2 style={styles.sectionTitle}>CHANGE PASSWORD</h2>

          {pwMsg && (
            <div style={pwMsg.type === 'success' ? styles.msgSuccess : styles.msgError}>
              {pwMsg.text}
            </div>
          )}

          <div style={styles.row1}>
            <label style={styles.label}>CURRENT PASSWORD</label>
            <div style={{ position: 'relative' }}>
              <input
                style={{ ...styles.input, paddingRight: '44px' }}
                type={showPwCurrent ? 'text' : 'password'}
                value={pwForm.currentPassword}
                onChange={e => setPwForm({ ...pwForm, currentPassword: e.target.value })}
                placeholder="Enter current password"
              />
              <button type="button" onClick={() => setShowPwCurrent(v => !v)} style={eyeBtn} tabIndex={-1}>{showPwCurrent ? '🙈' : '👁️'}</button>
            </div>
          </div>
          <div style={styles.row2}>
            <div>
              <label style={styles.label}>NEW PASSWORD</label>
              <div style={{ position: 'relative' }}>
                <input
                  style={{ ...styles.input, paddingRight: '44px' }}
                  type={showPwNew ? 'text' : 'password'}
                  value={pwForm.newPassword}
                  onChange={e => setPwForm({ ...pwForm, newPassword: e.target.value })}
                  placeholder="At least 8 characters"
                />
                <button type="button" onClick={() => setShowPwNew(v => !v)} style={eyeBtn} tabIndex={-1}>{showPwNew ? '🙈' : '👁️'}</button>
              </div>
            </div>
            <div>
              <label style={styles.label}>CONFIRM NEW PASSWORD</label>
              <div style={{ position: 'relative' }}>
                <input
                  style={{ ...styles.input, paddingRight: '44px' }}
                  type={showPwConfirm ? 'text' : 'password'}
                  value={pwForm.confirmPassword}
                  onChange={e => setPwForm({ ...pwForm, confirmPassword: e.target.value })}
                  placeholder="Repeat new password"
                />
                <button type="button" onClick={() => setShowPwConfirm(v => !v)} style={eyeBtn} tabIndex={-1}>{showPwConfirm ? '🙈' : '👁️'}</button>
              </div>
            </div>
          </div>
          <button
            className="jf-biz-hover"
            style={styles.saveBtn}
            onClick={handleChangePassword}
            disabled={pwSaving}
          >
            {pwSaving ? 'SAVING...' : 'UPDATE PASSWORD'}
          </button>

          <h2 style={styles.sectionTitle}>CHANGE LOGIN EMAIL</h2>

          {currentEmail && (
            <div style={styles.currentEmailRow}>CURRENT LOGIN EMAIL: {currentEmail}</div>
          )}

          {emMsg && (
            <div style={emMsg.type === 'success' ? styles.msgSuccess : styles.msgError}>
              {emMsg.text}
            </div>
          )}

          <div style={styles.row1}>
            <label style={styles.label}>CONFIRM PASSWORD</label>
            <div style={{ position: 'relative' }}>
              <input
                style={{ ...styles.input, paddingRight: '44px' }}
                type={showEmPw ? 'text' : 'password'}
                value={emForm.currentPassword}
                onChange={e => setEmForm({ ...emForm, currentPassword: e.target.value })}
                placeholder="Enter your current password to confirm"
              />
              <button type="button" onClick={() => setShowEmPw(v => !v)} style={eyeBtn} tabIndex={-1}>{showEmPw ? '🙈' : '👁️'}</button>
            </div>
          </div>
          <div style={styles.row1}>
            <label style={styles.label}>NEW LOGIN EMAIL</label>
            <input
              style={styles.input}
              type="email"
              value={emForm.newEmail}
              onChange={e => setEmForm({ ...emForm, newEmail: e.target.value })}
              placeholder="Enter new email address"
            />
          </div>
          <button
            className="jf-biz-hover"
            style={styles.saveBtn}
            onClick={handleChangeEmail}
            disabled={emSaving}
          >
            {emSaving ? 'SAVING...' : 'UPDATE LOGIN EMAIL'}
          </button>
        </div>

      </div>
    </div>
  );
};

export default BusinessSettings;
